import { Currency } from "_shared/money/currencies/currency";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import Percent from "_shared/percentages/percent";
import type Session from "users/__session/session";
import {
  type ProposalTemplateDocumentJSON,
  type ProposalTemplateJSON,
  ProposalTemplateJSONZ,
  type ProposalTemplateTeamJSON,
} from "work/proposal-templates/_schema/proposal-template-schema";
import ProposalTeamReuse from "work/proposals/drafts-from-previous/proposal-team-reuse";
import WorkDocument from "work/work-agreements/contracts/work-document";

export enum ProposalTemplateParty {
  Client = "Client",
  Vendor = "Vendor",
}

/** The side of a proposal the session is working on, which is the side a template is saved from and used on. */
export function proposalTemplatePartyOf(session: Pick<Session, "context">): ProposalTemplateParty {
  return session.context?.viewingAsVendor ? ProposalTemplateParty.Vendor : ProposalTemplateParty.Client;
}

const EXAMPLE_FIRM_ID = "11111111-1111-4111-8111-111111111111";
const EXAMPLE_LEADER_ID = "22222222-2222-4222-8222-222222222222";
const EXAMPLE_MEMBER_ID = "33333333-3333-4333-8333-333333333333";

export type ProposalTemplateTeam = {
  leader?: EntityMember;
  members: EntityMember[];
  marketplaceId?: Guid;
};

/**
 * A whole proposal saved as a named starting point for new drafts (#6129).
 *
 * It holds one side of a proposal, the side it was saved from: that side's team, policies and
 * template lists, with the fee schedule, discount, conflicts documents and settings. It can also hold
 * the other party's team and the team templates applied to it, when the saver chose to keep them
 * (#6637). It has no dates and no history. It is private to whoever saved it.
 */
export default class ProposalTemplate {
  /**
   * A vendor template as the API answers it, for tests and stories: a leader and one member, one staffed
   * rate, one live policy, one live conflicts check, and one policy deleted since it was saved.
   */
  public static exampleJSON(overrides: Partial<ProposalTemplateJSON> = {}): ProposalTemplateJSON {
    return {
      id: "44444444-4444-4444-8444-444444444444",
      name: "Standard filing",
      party: "Vendor",
      created: "2026-10-06T12:00:00Z",
      proposalName: "Patent prosecution",
      description: "Filing and prosecution",
      negotiable: false,
      teamRestricted: true,
      conflictsCheckWaived: false,
      team: {
        leader: { userId: EXAMPLE_LEADER_ID, legalEntityId: EXAMPLE_FIRM_ID },
        members: [{ userId: EXAMPLE_MEMBER_ID, legalEntityId: EXAMPLE_FIRM_ID }],
        marketplaceId: "55555555-5555-4555-8555-555555555555",
      },
      feeSchedule: [
        {
          id: "66666666-6666-4666-8666-666666666666",
          name: "Filing",
          feeRate: 4200,
          feeCurrency: Currency.USD,
          teamMember: {
            userId: EXAMPLE_MEMBER_ID,
            legalEntityId: EXAMPLE_FIRM_ID,
          },
        },
      ],
      discount: 0.1,
      teamTemplateIds: ["77777777-7777-4777-8777-777777777777"],
      feeScheduleTemplateIds: ["88888888-8888-4888-8888-888888888888"],
      counterpartyTeam: null,
      counterpartyTeamTemplateIds: [],
      conflicts: [
        {
          documentId: "99999999-9999-4999-8999-999999999999",
          isAvailable: true,
          documentInfo: {
            id: "99999999-9999-4999-8999-999999999999",
            name: "Conflicts check",
            fileType: "application/pdf",
          },
        },
      ],
      policies: [
        {
          documentId: "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa",
          isAvailable: true,
          documentInfo: {
            id: "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa",
            name: "Engagement terms",
            fileType: "application/pdf",
          },
        },
        {
          documentId: "bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbbb",
          isAvailable: false,
          documentInfo: null,
        },
      ],
      teamReuse: null,
      ...overrides,
    } as ProposalTemplateJSON;
  }

  public static fromJSON(json: unknown): ProposalTemplate {
    const parsed = ProposalTemplateJSONZ.parse(json);
    const documents = (entries: ProposalTemplateDocumentJSON[]) =>
      entries.flatMap((entry) =>
        entry.isAvailable && entry.documentInfo
          ? [
              WorkDocument.fromJSON({
                ...entry.documentInfo,
                id: entry.documentId,
                isAvailable: true,
              }),
            ]
          : [],
      );
    return new ProposalTemplate({
      id: new Guid(parsed.id),
      name: parsed.name,
      party: parsed.party === "Vendor" ? ProposalTemplateParty.Vendor : ProposalTemplateParty.Client,
      created: new Date(parsed.created),
      proposalName: parsed.proposalName,
      description: parsed.description,
      negotiable: parsed.negotiable,
      teamRestricted: parsed.teamRestricted,
      conflictsCheckWaived: parsed.conflictsCheckWaived,
      team: teamFromJSON(parsed.team),
      counterpartyTeam: teamFromJSON(parsed.counterpartyTeam),
      feeSchedule: parsed.feeSchedule.flatMap((category) => {
        const parsedCategory = FeeScheduleCategory.fromJSON(category);
        return parsedCategory instanceof FeeScheduleCategory ? [parsedCategory] : [];
      }),
      discount: new Percent(parsed.discount),
      teamTemplateIds: parsed.teamTemplateIds.map((id) => new Guid(id)),
      feeScheduleTemplateIds: parsed.feeScheduleTemplateIds.map((id) => new Guid(id)),
      counterpartyTeamTemplateIds: parsed.counterpartyTeamTemplateIds.map((id) => new Guid(id)),
      conflicts: documents(parsed.conflicts),
      policies: documents(parsed.policies),
      unavailableDocumentCount: [...parsed.conflicts, ...parsed.policies].filter(
        (entry) => !entry.isAvailable || !entry.documentInfo,
      ).length,
      teamReuse: parsed.teamReuse ? ProposalTeamReuse.fromJSON(parsed.teamReuse) : undefined,
    });
  }

  public static fromJSONArray(json: unknown): ProposalTemplate[] {
    return Array.isArray(json) ? json.map((entry) => ProposalTemplate.fromJSON(entry)) : [];
  }

  public readonly id: Guid;
  /** The template's own name, which its owner picks it by. */
  public readonly name: string;
  public readonly party: ProposalTemplateParty;
  public readonly created: Date;
  /** The name a draft started from it begins with. */
  public readonly proposalName: string;
  public readonly description: string;
  public readonly negotiable: boolean;
  public readonly teamRestricted: boolean;
  public readonly conflictsCheckWaived: boolean;
  public readonly team?: ProposalTemplateTeam;
  public readonly feeSchedule: FeeScheduleCategory[];
  public readonly discount: Percent;
  public readonly teamTemplateIds: Guid[];
  public readonly feeScheduleTemplateIds: Guid[];
  /** The other party's team, when the saver kept it (#6637). */
  public readonly counterpartyTeam?: ProposalTemplateTeam;
  /** The team templates applied to the other party's team, when the saver kept them. */
  public readonly counterpartyTeamTemplateIds: Guid[];
  /** The template's own copies of the conflicts documents that still exist. */
  public readonly conflicts: WorkDocument[];
  /** The template's own copies of its side's policies that still exist. */
  public readonly policies: WorkDocument[];
  /** How many of its documents have been deleted since it was saved. */
  public readonly unavailableDocumentCount: number;
  /** Who on its team cannot be carried into a new draft. Only a single template's read carries it. */
  public readonly teamReuse?: ProposalTeamReuse;

  public constructor(params: {
    id: Guid;
    name: string;
    party: ProposalTemplateParty;
    created: Date;
    proposalName: string;
    description: string;
    negotiable: boolean;
    teamRestricted: boolean;
    conflictsCheckWaived: boolean;
    team?: ProposalTemplateTeam;
    feeSchedule: FeeScheduleCategory[];
    discount: Percent;
    teamTemplateIds: Guid[];
    feeScheduleTemplateIds: Guid[];
    counterpartyTeam?: ProposalTemplateTeam;
    counterpartyTeamTemplateIds?: Guid[];
    conflicts: WorkDocument[];
    policies: WorkDocument[];
    unavailableDocumentCount: number;
    teamReuse?: ProposalTeamReuse;
  }) {
    this.id = params.id;
    this.name = params.name;
    this.party = params.party;
    this.created = params.created;
    this.proposalName = params.proposalName;
    this.description = params.description;
    this.negotiable = params.negotiable;
    this.teamRestricted = params.teamRestricted;
    this.conflictsCheckWaived = params.conflictsCheckWaived;
    this.team = params.team;
    this.feeSchedule = [...params.feeSchedule];
    this.discount = params.discount;
    this.teamTemplateIds = [...params.teamTemplateIds];
    this.feeScheduleTemplateIds = [...params.feeScheduleTemplateIds];
    this.counterpartyTeam = params.counterpartyTeam;
    this.counterpartyTeamTemplateIds = [...(params.counterpartyTeamTemplateIds ?? [])];
    this.conflicts = [...params.conflicts];
    this.policies = [...params.policies];
    this.unavailableDocumentCount = params.unavailableDocumentCount;
    this.teamReuse = params.teamReuse;
  }

  /** Whether a draft can be started from it in this session: only on the side it was saved from. */
  public isUsableIn(session: Pick<Session, "context">): boolean {
    return this.party === proposalTemplatePartyOf(session);
  }
}

function teamFromJSON(team: ProposalTemplateTeamJSON | null | undefined): ProposalTemplateTeam | undefined {
  if (!team) return undefined;
  return {
    leader: EntityMember.fromJSON(team.leader) ?? undefined,
    members: team.members.flatMap((member) => EntityMember.fromJSON(member) ?? []),
    marketplaceId: team.marketplaceId ? new Guid(team.marketplaceId) : undefined,
  };
}

export type { ProposalTemplateJSON };
